import React, { useCallback, useEffect, useRef, useState } from "react";
import Select from 'react-select';
import QRCodeStyling, { Options } from "qr-code-styling";
import { motion, AnimatePresence } from "framer-motion";
import { Edit, Trash2, Download, QrCode, Plus, ImagePlus, X, Check } from "lucide-react";
import PreviewUrlBox from "./PreviewUrlBox";
import TableFormModal from "./TableFormModal";

import {
  getTablesByRestaurant,
  createTable,
  updateTable,
  deleteTable
} from "../api/tableApi";

import "../styles/TableManagement.css";

interface TableItem {
  id: string;
  tableLabel: string;
  qrCodeUrl?: string;
  seatCapacity?: number;
  isQRMenuOrderEnabled?: boolean;
}

interface ThemeOption {
  name: string;
  options: Partial<Options>;
}

/* ---------------------------- Themes ----------------------------- */
const QR_THEMES: ThemeOption[] = [
  {
    name: "Orange Blaze",
    options: {
      dotsOptions: {
        type: "rounded",
        gradient: {
          type: "linear",
          rotation: 0.2,
          colorStops: [
            { offset: 0, color: "#ff512f" },
            { offset: 0.5, color: "#ff9800" },
            { offset: 1, color: "#ffb347" }
          ]
        }
      },
      backgroundOptions: { color: "#fff8f0" },
      cornersSquareOptions: { type: "extra-rounded", color: "#ff512f" }
    }
  },
  {
    name: "Tangerine Dream",
    options: {
      dotsOptions: {
        type: "classy-rounded",
        gradient: {
          type: "linear",
          rotation: 0.5,
          colorStops: [
            { offset: 0, color: "#ff9800" },
            { offset: 1, color: "#ff512f" }
          ]
        }
      },
      backgroundOptions: { color: "#fff3e0" },
      cornersSquareOptions: { type: "extra-rounded", color: "#ff9800" }
    }
  },
  {
    name: "Amber Glow",
    options: {
      dotsOptions: {
        type: "dots",
        gradient: {
          type: "linear",
          rotation: 0.8,
          colorStops: [
            { offset: 0, color: "#ffb347" },
            { offset: 1, color: "#ff512f" }
          ]
        }
      },
      backgroundOptions: { color: "#fffbe6" },
      cornersSquareOptions: { type: "extra-rounded", color: "#ffb347" }
    }
  },
  {
    name: "Coral Pop",
    options: {
      dotsOptions: {
        type: "extra-rounded",
        gradient: {
          type: "radial",
          rotation: 1,
          colorStops: [
            { offset: 0, color: "#ff7e5f" },
            { offset: 1, color: "#feb47b" }
          ]
        }
      },
      backgroundOptions: { color: "#fff5f0" },
      cornersSquareOptions: { type: "square", color: "#ff7e5f" }
    }
  },
  {
    name: "Classic Black",
    options: {
      dotsOptions: { color: "#111827", type: "rounded" },
      backgroundOptions: { color: "#ffffff" },
      cornersSquareOptions: { type: "square", color: "#111827" }
    }
  },
  {
    name: "Oceanic Twilight",
    options: {
      dotsOptions: {
        type: "rounded",
        gradient: {
          type: "linear",
          rotation: 0.7,
          colorStops: [
            { offset: 0, color: "#4facfe" },
            { offset: 1, color: "#00f2fe" }
          ]
        }
      },
      backgroundOptions: { color: "#f0f8ff" },
      cornersSquareOptions: { type: "extra-rounded", color: "#4facfe" }
    }
  },
  {
    name: "Emerald Forest",
    options: {
      dotsOptions: {
        type: "extra-rounded",
        gradient: {
          type: "linear",
          rotation: 0.1,
          colorStops: [
            { offset: 0, color: "#11998e" },
            { offset: 1, color: "#38ef7d" }
          ]
        }
      },
      backgroundOptions: { color: "#f0fdf4" },
      cornersSquareOptions: { type: "dots", color: "#11998e" }
    }
  },
  {
    name: "Mystic Magenta",
    options: {
      dotsOptions: {
        type: "classy",
        gradient: {
          type: "radial",
          rotation: 0,
          colorStops: [
            { offset: 0, color: "#a80077" },
            { offset: 1, color: "#6610f2" }
          ]
        }
      },
      backgroundOptions: { color: "#f8f0ff" },
      cornersSquareOptions: { type: "extra-rounded", color: "#a80077" }
    }
  },
  {
    name: "Stormy Grey",
    options: {
      dotsOptions: { color: "#4a5568", type: "extra-rounded" },
      backgroundOptions: { color: "#e2e8f0" },
      cornersSquareOptions: { type: "dot", color: "#4a5568" }
    }
  },
  {
    name: "Midnight Sun",
    options: {
      dotsOptions: {
        type: "square",
        gradient: {
          type: "linear",
          rotation: 0.3,
          colorStops: [
            { offset: 0, color: "#ffb347" },
            { offset: 1, color: "#ff7e5f" }
          ]
        }
      },
      backgroundOptions: { color: "#1a202c" },
      cornersSquareOptions: { type: "extra-rounded", color: "#ffb347" }
    }
  }
];

const getRestaurantGuid = (): string => localStorage.getItem("restaurantGuid") || "restaurant_12345";

/* ---------------------------- Helpers ----------------------------- */
function showGlobalToast(message: string, type: "success" | "error" = "success") {
  window.dispatchEvent(new CustomEvent("global-toast", { detail: { message, type } }));
}

/* Create a QRCodeStyling instance with the supplied params */
const createQrInstance = (data: string, options: Partial<Options>, size = 420) =>
  new QRCodeStyling({ width: size, height: size, type: "svg", data, ...options } as Options);

/* ---------------------------- Component ----------------------------- */
const TableManagement: React.FC = () => {
  const [tables, setTables] = useState<TableItem[]>([]);
  const [loading, setLoading] = useState(true);
  // modal form state
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [formInitial, setFormInitial] = useState<any | null>(null);
  // UI state
  const [selectedThemeName, setSelectedThemeName] = useState(QR_THEMES[0].name);
  const [logoUrl, setLogoUrl] = useState("");
  const [previewingTableId, setPreviewingTableId] = useState<string | null>(null);
  const [deletingTableId, setDeletingTableId] = useState<string | null>(null);

  // Modal QR instance (created only when preview opens)
  const modalQrRef = useRef<QRCodeStyling | null>(null);
  const modalContainerRef = useRef<HTMLDivElement | null>(null);

  // Preview URL (shown in modal and copyable)
  const previewUrl = previewingTableId ? `${window.location.origin}/customer/${getRestaurantGuid()}/${previewingTableId}` : "";

  const logoInputRef = useRef<HTMLInputElement | null>(null);

  const getThemeOptions = useCallback((themeName: string) => QR_THEMES.find(t => t.name === themeName)?.options ?? QR_THEMES[0].options, []);

  // For react-select
  const themeOptions = QR_THEMES.map(t => ({ value: t.name, label: t.name }));
  const selectedTheme = themeOptions.find(opt => opt.value === selectedThemeName);


  /* Fetch tables */
  const fetchTables = useCallback(async () => {
    setLoading(true);
    try {
      const restaurantGuid = getRestaurantGuid();
      const res = await getTablesByRestaurant(restaurantGuid);
      const data = (res && (res.data ?? res)) as TableItem[];
      setTables(data || []);
    } catch (err) {
      console.error(err);
      showGlobalToast("Failed to load tables", "error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTables(); }, [fetchTables]);

  /* Logo uploads */
  const handleLogoUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => setLogoUrl(reader.result as string);
    reader.readAsDataURL(file);
  }, []);

  /* CRUD */
  /* CRUD */

  const openAddForm = useCallback(() => { setFormInitial(null); setIsFormOpen(true); }, []);
  const openEditForm = useCallback((t: TableItem) => {
    setFormInitial({
      id: t.id,
      tableLabel: t.tableLabel,
      seatCapacity: t.seatCapacity ?? 0,
      isQRMenuOrderEnabled: t.isQRMenuOrderEnabled ?? false,
    });
    setIsFormOpen(true);
  }, []);
  const closeForm = useCallback(() => { setIsFormOpen(false); setFormInitial(null); }, []);
  const onDelete = useCallback((id: string) => setDeletingTableId(id), []);

  const confirmDelete = useCallback(async () => {
    if (!deletingTableId) return;
    try {
      await deleteTable(deletingTableId);
      showGlobalToast("Table deleted", "success");
      await fetchTables();
    } catch (err) {
      console.error(err);
      showGlobalToast("Failed to delete", "error");
    } finally {
      setDeletingTableId(null);
    }
  }, [deletingTableId, fetchTables]);

  /* --------------------------- Preview (create QR on demand) --------------------------- */
  const openPreview = useCallback(async (tableId: string) => {
    setPreviewingTableId(tableId);
    // create modal QR instance once modal container is mounted (see effect)
  }, []);

  const closePreview = useCallback(() => {
    setPreviewingTableId(null);
    // cleanup instance
    if (modalQrRef.current) {
      try { modalQrRef.current = null; }
      catch { }
    }
    if (modalContainerRef.current) {
      modalContainerRef.current.innerHTML = "";
    }
  }, []);

  // Lazy QR creation: only create QR instance when previewingTableId is set and modal is open
  useEffect(() => {
    if (!previewingTableId) {
      // Clean up QR and DOM when modal closes
      if (modalContainerRef.current) modalContainerRef.current.innerHTML = "";
      modalQrRef.current = null;
      return;
    }
    const table = tables.find(t => t.id === previewingTableId);
    if (!table) return;
    const uiDomain = window.location.origin;
    const data = `${uiDomain}/customer/${getRestaurantGuid()}/${table.id}`;
    const baseOpts = getThemeOptions(selectedThemeName);
    // For preview, render QR with transparent background so it sits cleanly on the modal
    const opts = {
      image: logoUrl || undefined,
      imageOptions: { crossOrigin: "anonymous", margin: 12 },
      ...baseOpts,
      backgroundOptions: { ...(baseOpts.backgroundOptions || {}), color: "transparent" }
    };
    // Clean up previous
    if (modalContainerRef.current) modalContainerRef.current.innerHTML = "";
  // create a smaller preview instance (matches modal QR box size)
  modalQrRef.current = createQrInstance(data, opts, 260);
    if (modalContainerRef.current && modalQrRef.current) {
      modalQrRef.current.append(modalContainerRef.current);
    }
    // Cleanup on close or dependency change
    return () => {
      if (modalContainerRef.current) modalContainerRef.current.innerHTML = "";
      modalQrRef.current = null;
    };
  }, [previewingTableId, selectedThemeName, logoUrl, tables, getThemeOptions]);

  /* Download high-res from preview (or directly) - lazy QR creation */
  const handleDownload = useCallback(async (tableId: string) => {
    const table = tables.find(t => t.id === tableId);
    if (!table) return;
    const uiDomain = window.location.origin;
    const data = `${uiDomain}/customer/${getRestaurantGuid()}/${table.id}`;
    const opts = { image: logoUrl || undefined, imageOptions: { crossOrigin: "anonymous", margin: 12 }, ...getThemeOptions(selectedThemeName) };
    // Lazy create QR instance for download only
    const tmp = createQrInstance(data, opts, 1000);
    try {
      await tmp.download({ name: `table-qr-${table.tableLabel}`, extension: "png" });
    } catch (err) {
      console.error(err);
      showGlobalToast("Download failed", "error");
    }
  }, [tables, logoUrl, getThemeOptions, selectedThemeName]);

  /* Add / Edit form save handler (called from modal) */
  const handleFormSave = useCallback(async (vals: any) => {
    // vals: { id?, tableLabel, seatCapacity, isQRMenuOrderEnabled }
    const trimmed = vals.tableLabel.trim();
    // duplicate check
    const isDuplicate = tables.some(t => t.tableLabel.trim().toLowerCase() === trimmed.toLowerCase() && t.id !== vals.id);
    if (isDuplicate) {
      showGlobalToast("Table label already exists. Please use a unique name.", "error");
      return false;
    }
    try {
      const restaurantGuid = getRestaurantGuid();
      if (vals.id) {
        await updateTable(vals.id, { restaurantGuid, tableLabel: trimmed, seatCapacity: String(vals.seatCapacity), isQRMenuOrderEnabled: vals.isQRMenuOrderEnabled });
        showGlobalToast("Table updated", "success");
      } else {
        await createTable({ restaurantGuid, tableLabel: trimmed, seatCapacity: String(vals.seatCapacity), isQRMenuOrderEnabled: vals.isQRMenuOrderEnabled });
        showGlobalToast("Table added", "success");
      }
      await fetchTables();
      return true;
    } catch (err) {
      console.error(err);
      showGlobalToast("Failed to save table", "error");
      return false;
    }
  }, [fetchTables, tables]);

  /* --------------------------- Small Card UI + Accessibility --------------------------- */
  return (
    //<div className="tm-root bg-white text-[#23160d] dark:bg-[#18181b] dark:text-[#fff8f0] min-h-screen">
    <div className="w-full mx-auto px-2 sm:px-4 md:px-6 lg:px-0 py-4 bg-gray-100 text-gray-900 dark:bg-gray-900 dark:text-gray-100 min-h-full overflow-auto">

      <header className="tm-header">
        <div className="tm-headline">
          <QrCode className="tm-qr-icon text-[#ff512f] dark:text-[#ff9800]" />
          <h1>Table Management</h1>
        </div>
        <p className="tm-sub text-[#b26a3a] dark:text-[#e0a97f]">Streamline table management and generate instant, table-specific QR codes for your digital menu.</p>
        <p className="mt-1 text-sm text-[#d97706] dark:text-[#fbbf24] font-medium">
          ✨ Looking for something unique? We craft custom QR codes with logos, vibrant designs, and even 3D-printed styles — contact us to elevate your brand!
        </p>
      </header>

      <main className="w-full max-w-none px-0">
        <section className="tm-panel w-full bg-white dark:bg-[#18181b] mb-8 rounded-xl shadow-md p-6">
          <h2 className="mb-6 text-lg font-semibold text-gray-800 dark:text-gray-100">QR Customization & Create Table</h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 w-full items-center">

            {/* LEFT (spans 2): Theme select + Logo Upload grouped into a card */}
            <div className="md:col-span-2">
              <div className="flex flex-col sm:flex-row sm:items-center gap-4 bg-orange-50/30 dark:bg-[#2a1a10] p-4 rounded-xl">
                <div className="flex-1 min-w-0">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-2 block">Choose Theme</label>
                  <Select
                    classNamePrefix="qr-theme-select"
                    options={themeOptions}
                    value={selectedTheme}
                    onChange={opt => setSelectedThemeName(opt?.value || QR_THEMES[0].name)}
                    isSearchable={false}
                    classNames={{
                      control: ({ isFocused }) =>
                        `min-h-[40px] rounded-xl text-[14px] border transition-all w-full ${isFocused
                          ? 'border-orange-400 ring-2 ring-orange-400'
                          : 'border-gray-300 dark:border-gray-600'
                        } bg-white dark:bg-[#18181b] text-gray-900 dark:text-gray-100`,
                      menu: () => 'rounded-xl shadow-lg z-20 bg-white dark:bg-[#23272f]',
                      option: ({ isSelected, isFocused }) =>
                        `cursor-pointer px-3 py-2 ${isSelected
                          ? 'bg-orange-50 dark:bg-[#2d1c0e] font-semibold'
                          : isFocused
                            ? 'bg-orange-50 dark:bg-[#3a2412]'
                            : ''
                        } text-gray-900 dark:text-gray-100`,
                      singleValue: () => 'text-gray-900 dark:text-gray-100',
                      dropdownIndicator: () => 'text-[#b26a3a] dark:text-[#ff9800]',
                      indicatorSeparator: () => 'hidden',
                    }}
                    styles={{
                      control: (base) => ({
                        ...base,
                        width: selectedTheme?.label.length && selectedTheme.label.length < 20
                          ? `${selectedTheme.label.length * 1.1 + 6}ch`
                          : '100%',
                        minHeight: 40,
                        borderRadius: 12,
                        fontSize: 14,
                        transition: 'width 0.2s',
                      }),
                    }}
                  />
                </div>

                <div className="flex items-center gap-3">
                  <label htmlFor="logo-upload" className="flex items-center px-2 py-1 md:px-3 md:py-2 rounded-lg bg-gradient-to-r from-[#ff512f] to-[#ff9800] dark:from-[#ff9800] dark:to-[#ff512f] text-white cursor-pointer shadow-sm hover:shadow-md transition text-sm md:text-base">
                    <ImagePlus size={16} className="mr-2" />
                    {logoUrl ? "Change Logo" : "Upload Logo"}
                  </label>
                  <input id="logo-upload" ref={logoInputRef} onChange={handleLogoUpload} type="file" accept="image/*" className="sr-only" />
                  {logoUrl && (
                    <div className="tm-logo-preview rounded-lg overflow-hidden shadow-sm">
                      <img src={logoUrl} alt="logo preview" className="object-cover w-12 h-12" />
                      <button className="tm-logo-remove absolute top-0 right-0 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center hover:bg-red-600 transition" onClick={() => setLogoUrl("")}> <X size={12} /></button>
                    </div>
                  )}
                </div>
              </div>
              <p className="mt-2 text-sm text-[#b26a3a] dark:text-[#e0a97f]">Pick a theme and upload a logo to preview brandable QR codes instantly.</p>
            </div>

            {/* RIGHT: Create Table (single column, vertically centered) */}
            <div className="md:col-span-1 flex items-center justify-center md:justify-end">
              <div className="w-full max-w-xs">
                <label className="sr-only">Create Table</label>
                <button type="button" onClick={openAddForm} className="w-full flex items-center justify-center gap-2 px-3 py-2 md:px-4 md:py-3 text-sm md:text-base rounded-xl text-white bg-gradient-to-r from-[#ff512f] to-[#ff9800] dark:from-[#ff9800] dark:to-[#ff512f] shadow-lg hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-orange-400 transition">
                  <Plus size={16} />
                  <span className="font-medium">Create Table</span>
                </button>
                <p className="mt-2 text-xs text-gray-600 dark:text-gray-400 hidden md:block">Quickly add a table with ordering enabled by default.</p>
              </div>
            </div>
          </div>
        </section>
        <section className="tm-list">
          {loading ? (
            <div className="tm-empty text-[#b26a3a] dark:text-[#e0a97f]">Loading tables...</div>
          ) : tables.length === 0 ? (
            <div className="tm-empty text-[#b26a3a] dark:text-[#e0a97f]">No tables yet — add one above.</div>
          ) : (
            <div className="tm-grid-cards responsive">
              {tables.map(t => (
                <article key={t.id} className="tm-card bg-orange-50 dark:bg-[#23160d]">
                  <h3 className="tm-card-title text-[#ff512f] dark:text-[#ff9800]">{t.tableLabel}</h3>
                  <div className="tm-card-actions-row">
                    <button className="icon-btn text-[#ff512f] dark:text-[#ffb347] hover:bg-[#fff3e0] dark:hover:bg-[#2d1c0e] hover:text-[#b34700] dark:hover:text-[#ff9800]" title="Preview" onClick={() => openPreview(t.id)}><QrCode /></button>
                    <button className="icon-btn text-[#ff512f] dark:text-[#ffb347] hover:bg-[#fff3e0] dark:hover:bg-[#2d1c0e] hover:text-[#b34700] dark:hover:text-[#ff9800]" title="Download" onClick={() => handleDownload(t.id)}><Download /></button>
                    <button className="icon-btn text-[#ff512f] dark:text-[#ffb347] hover:bg-[#fff3e0] dark:hover:bg-[#2d1c0e] hover:text-[#b34700] dark:hover:text-[#ff9800]" title="Edit" onClick={() => openEditForm(t)}><Edit /></button>
                    <button className="icon-btn danger text-[#ff512f] dark:text-[#ff512f] hover:bg-[#fff0f0] dark:hover:bg-[#2d1c0e] hover:text-[#b34700] dark:hover:text-[#ff9800]" title="Delete" onClick={() => onDelete(t.id)}><Trash2 /></button>
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>
      </main>

      {/* Preview modal (QR created only when opened) */}
      <AnimatePresence>
        {previewingTableId && (
          <motion.div className="tm-modal-backdrop" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={closePreview}>
            <motion.div
              className="tm-modal tm-modal-responsive bg-white dark:bg-[#23160d]"
              initial={{ scale: 0.98, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.98, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <header className="tm-modal-head border-b border-[#ffe0b2] dark:border-[#3a2412]">
                <h4>QR Preview</h4>
                <button className="icon-btn text-[#ff512f] dark:text-[#ffb347]" onClick={closePreview}><X /></button>
              </header>

              <div className="tm-modal-body">
                <div
                  ref={(el) => {
                    modalContainerRef.current = el;
                  }}
                  className="tm-modal-qr"
                />

                {/* Reusable URL box component */}
                <PreviewUrlBox url={previewUrl} />
              </div>

              <footer className="tm-modal-foot border-t border-[#ffe0b2] dark:border-[#3a2412]">
                <button className="tm-btn tm-btn-primary bg-gradient-to-r from-[#ff512f] to-[#ff9800] text-white dark:from-[#ff9800] dark:to-[#ff512f]" onClick={() => previewingTableId && handleDownload(previewingTableId)}><Download className="mr-1" />Download</button>
                <button className="tm-btn tm-btn-ghost bg-[#f3f4f6] text-[#111827] dark:bg-[#2d1c0e] dark:text-[#ffb347]" onClick={closePreview}>Close</button>
              </footer>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

  {/* Add / Edit Table modal */}
  <TableFormModal isOpen={isFormOpen} initial={formInitial} onClose={closeForm} onSave={handleFormSave} />

      {/* Delete confirm modal */}
      <AnimatePresence>
        {deletingTableId && (
          <motion.div className="tm-modal-backdrop" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setDeletingTableId(null)}>
            <motion.div className="tm-modal bg-white dark:bg-[#23160d]" onClick={(e) => e.stopPropagation()}>
              <div className="text-center">
                <Trash2 size={48} className="text-red-500 mx-auto" />
                <h4 className="mt-4">Delete Table?</h4>
                <p className="text-[#b26a3a] dark:text-[#e0a97f]">This action cannot be undone.</p>
              </div>
              <footer className="tm-modal-foot border-t border-[#ffe0b2] dark:border-[#3a2412]">
                <button className="tm-btn tm-btn-danger bg-[#ff512f] text-white dark:bg-[#ff512f] dark:text-[#fff8f0]" onClick={confirmDelete}><Check className="mr-2" />Yes, delete</button>
                <button className="tm-btn tm-btn-ghost bg-[#f3f4f6] text-[#111827] dark:bg-[#2d1c0e] dark:text-[#ffb347]" onClick={() => setDeletingTableId(null)}>Cancel</button>
              </footer>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

    </div>
  );
};

export default TableManagement;